import { Controls, Player } from '@lottiefiles/react-lottie-player';
import { useOktaAuth } from '@okta/okta-react';
import { useAtomValue } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router';

import type { User } from '../@types/global';
import { isMagicLinkUserAtom } from '../modules/login/atoms';
import {
  hasNoValidLocations,
  isMultiPracticeUser,
} from '../modules/login/login-utils';
import { usePreferences } from '../react-query/usePreferences';
import routes from '../routes/routes.constants';
import { useDirectorPracticeView } from '../utils/hooks/useDirectorPracticeView';
import { mixpanelEvents, useTrackEvent } from '../utils/mixpanel';

const AppLoader = () => {
  const { oktaAuth } = useOktaAuth();
  const user: User = useSelector((state: RootStateOrAny) => state?.login?.user);

  const { directorViewPracticeId } = useDirectorPracticeView();
  const { trackEvent } = useTrackEvent();
  const isMagicLinkUser = useAtomValue(isMagicLinkUserAtom);
  const location = useLocation();
  const isInsightsRoute = location.pathname.includes('insights');

  const {
    data: prefs,
    isLoading: isLoadingPreferences,
    isFetching: isFetchingPreferences,
  } = usePreferences();

  if (!user || (isLoadingPreferences && isFetchingPreferences)) {
    return (
      <>
        <Player
          autoplay
          loop
          src="/assets/images/videa-load.json"
          style={{ height: '340px', width: '600px' }}
        >
          <Controls visible={false} buttons={[]} />
        </Player>
      </>
    );
  } else if (
    oktaAuth.getIdToken() &&
    isMultiPracticeUser(user) &&
    !directorViewPracticeId &&
    !isInsightsRoute
  ) {
    return <Redirect to={routes.SWITCHBOARD} />;
  } else if (
    oktaAuth.getIdToken() &&
    isMultiPracticeUser(user) &&
    !directorViewPracticeId &&
    isInsightsRoute
  ) {
    return <Redirect to={routes.INSIGHTS} />;
  } else if (oktaAuth.getIdToken() && hasNoValidLocations(user)) {
    return <Redirect to={routes.UNAUTHORIZED} />;
  } else if (oktaAuth.getIdToken() && prefs?.standaloneClaimsEnabled) {
    return <Redirect to={routes.CLAIMS} />;
  } else if (oktaAuth.getIdToken() && prefs?.enableVoiceNotesStandalone) {
    return <Redirect to={routes.AMBIENT} />;
  } else if (oktaAuth.getIdToken()) {
    return <Redirect to={routes.UPLOADER} />;
  }

  if (isMagicLinkUser) {
    trackEvent(mixpanelEvents.magicLinkRedirectToLogin, {
      cause: 'app loader unauthenticated redirect',
    });
  }

  return (
    <Redirect
      to={{
        pathname: routes.LOGIN,
      }}
    />
  );
};

export default AppLoader;
